import { useState } from "react";
import { Globe, Menu } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useLanguage } from "../i18n/LanguageContext";
import { LANGUAGES } from "../i18n/translations";

const NAV_KEYS = ["services", "whyUs", "pricing", "process", "faq", "contact", "gdpr"];
const NAV_HREFS = {
  services: "#servicii",
  whyUs: "#de-ce-noi",
  pricing: "#preturi",
  process: "#proces",
  faq: "#intrebari",
  gdpr: "#gdpr",
  contact: "#contact",
};

export const LanguageSwitcher = ({ testIdPrefix }) => {
  const { language, setLanguage } = useLanguage();
  const current = LANGUAGES.find((l) => l.code === language);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 rounded-none border-[#e2e8f0] font-display font-semibold"
          data-testid={`${testIdPrefix}-language-switcher-button`}
        >
          <Globe className="h-4 w-4 text-[#1d6fd1]" />
          {current?.short}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-none">
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onSelect={() => setLanguage(lang.code)}
            className={language === lang.code ? "font-bold text-[#1d6fd1]" : ""}
            data-testid={`${testIdPrefix}-language-option-${lang.code}`}
          >
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Header = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 border-b border-[#e2e8f0] bg-white/90 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="#top" className="flex items-center" data-testid="header-logo-link">
          <img
            src="/assets/uba-logo-big.jpg"
            alt="UBA COMPANY"
            className="h-12 w-auto"
            data-testid="header-logo"
          />
        </a>

        <nav
          className="hidden items-center gap-7 lg:flex"
          data-testid="header-nav"
        >
          {NAV_KEYS.map((key) => (
            <a
              key={key}
              href={NAV_HREFS[key]}
              className="font-display text-sm font-semibold text-[#0d2b52] transition-colors hover:text-[#1d6fd1]"
              data-testid={`nav-link-${key}`}
            >
              {t.nav[key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher testIdPrefix="header" />
          <Button
            asChild
            className="hidden rounded-none bg-[#0d2b52] font-display font-semibold text-white hover:bg-[#1d6fd1] sm:inline-flex"
            data-testid="header-cta-button"
          >
            <a href="#contact">{t.nav.cta}</a>
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="rounded-none lg:hidden"
                aria-label={t.nav.menu}
                data-testid="mobile-menu-button"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="rounded-none" data-testid="mobile-menu">
              <div className="mt-8 flex flex-col gap-5">
                {NAV_KEYS.map((key) => (
                  <a
                    key={key}
                    href={NAV_HREFS[key]}
                    onClick={() => setOpen(false)}
                    className="font-display text-lg font-semibold text-[#0d2b52]"
                    data-testid={`mobile-nav-link-${key}`}
                  >
                    {t.nav[key]}
                  </a>
                ))}
                <Button
                  asChild
                  className="mt-4 rounded-none bg-[#0d2b52] font-display font-semibold text-white hover:bg-[#1d6fd1]"
                  data-testid="mobile-cta-button"
                >
                  <a href="#contact" onClick={() => setOpen(false)}>
                    {t.nav.cta}
                  </a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
